import React from "react";
import { MapPin } from "lucide-react";

// Landmark projects — real installations captured in the client's own BNI deck.
// Each tile pairs a genuine on-site photograph with the venue name + product line.
const LANDMARK_PROJECTS = [
  {
    venue: "Meridian by the Lawns",
    scope: "Broadloom ballroom carpet",
    image: "/media/carpet-broadloom-ballroom.jpg",
    tag: "Hospitality",
  },
  {
    venue: "Patanjali Auditorium",
    scope: "Custom auditorium carpet + wooden flooring",
    image: "/media/carpet-auditorium.jpg",
    tag: "Haridwar",
  },
  {
    venue: "Embassy Boulevard Villa",
    scope: "Textured designer wallpapers",
    image: "/media/wall-textured-hero.jpg",
    tag: "Residential",
  },
  {
    venue: "Omega Healthcare",
    scope: "Executive workstation carpet tiles",
    image: "/media/carpet-tiles-commercial.jpg",
    tag: "Corporate",
  },
  {
    venue: "DJ Sports Arena",
    scope: "Sports turf systems",
    image: "/media/turf-sports-arena.jpg",
    tag: "Sports",
  },
  {
    venue: "Haj Bhavan",
    scope: "Wooden flooring & finishing",
    image: "/media/floor-hardwood-herringbone.jpg",
    tag: "Public",
  },
];

// Secondary marquee — additional trusted names without dedicated photos yet.
const SUPPORTING_CLIENTS = [
  "ITC Hospitality",
  "Hotel Hubli",
  "RMZ Corporate",
  "Brigade Group",
  "Convention Centres of Karnataka",
  "23+ BNI Chapters",
];

const STATS = [
  { value: "4", suffix: "Decades", label: "of Trust & Excellence" },
  { value: "500+", suffix: "Projects", label: "Delivered Across South India" },
  { value: "23+", suffix: "BNI Chapters", label: "Supplying Regularly" },
  { value: "4.8★", suffix: "Google", label: "Verified Rating" },
];

export default function Clients() {
  return (
    <section
      className="relative py-20 lg:py-28 bg-slate-950 text-white overflow-hidden"
      data-testid="clients-section"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 h-[400px] w-[400px] rounded-full bg-amber-700/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[380px] w-[380px] rounded-full bg-amber-900/20 blur-3xl" />
        <div className="absolute inset-0 bg-grain opacity-25" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="eyebrow !text-amber-500 mb-4">Esteemed Clients · Landmark Projects</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium tracking-tight">
              Where our floors, walls & carpets have <span className="italic text-amber-500">gone before.</span>
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed text-[15px] sm:text-base max-w-xl">
              Four decades of installations across India&rsquo;s most demanding hospitality campuses, corporate HQs, auditoriums, healthcare hubs and luxury villas.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-amber-500 shrink-0">
            <span className="h-px w-10 bg-amber-500" />
            06 Signature Sites
          </div>
        </div>

        {/* Stat strip */}
        <ul
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-12"
          data-testid="clients-stats"
        >
          {STATS.map((s) => (
            <li
              key={s.value + s.suffix}
              className="border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 sm:p-6 hover:border-amber-500/40 transition-colors"
              data-testid={`clients-stat-${s.suffix.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <p className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-none">
                {s.value}
                <span className="ml-2 text-amber-500 text-xl sm:text-2xl lg:text-3xl font-normal">
                  {s.suffix}
                </span>
              </p>
              <p className="mt-3 text-[11px] sm:text-xs tracking-[0.22em] uppercase text-white/60 leading-relaxed">
                {s.label}
              </p>
            </li>
          ))}
        </ul>

        {/* Landmark projects grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
          data-testid="landmark-projects-grid"
        >
          {LANDMARK_PROJECTS.map((p) => (
            <article
              key={p.venue}
              className="group relative aspect-[4/3] overflow-hidden border border-white/10 bg-slate-900 hover:border-amber-500/50 transition-colors"
              data-testid={`landmark-project-${p.venue.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <img
                src={p.image}
                alt={`${p.venue} — ${p.scope}`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/45 to-transparent" />
              <span className="absolute top-4 left-4 inline-block bg-amber-700/90 text-white text-[10px] tracking-[0.24em] uppercase px-2.5 py-1 backdrop-blur-sm">
                {p.tag}
              </span>
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <div className="flex items-start gap-2 text-amber-500 mb-1.5">
                  <MapPin className="h-3.5 w-3.5 mt-1 shrink-0" />
                  <p className="text-[10px] tracking-[0.24em] uppercase">Installed by SGE</p>
                </div>
                <h3 className="font-display text-xl sm:text-2xl text-white leading-tight">
                  {p.venue}
                </h3>
                <p className="mt-1.5 text-white/75 text-sm leading-snug">
                  {p.scope}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Also-serving strip — bold pill cards for higher client viewership */}
        <div className="mt-14 pt-10 border-t border-white/10">
          <div className="text-center mb-8">
            <p className="text-[11px] tracking-[0.32em] uppercase text-amber-500 mb-3">
              Also Trusted By
            </p>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white font-medium leading-tight">
              Marquee names that <span className="italic text-amber-500">walk on our floors.</span>
            </h3>
          </div>
          <ul className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {SUPPORTING_CLIENTS.map((c) => (
              <li
                key={c}
                className="group inline-flex items-center gap-2.5 border border-amber-500/30 bg-gradient-to-br from-white/[0.06] to-white/[0.02] hover:from-amber-500/15 hover:to-amber-500/5 hover:border-amber-500/60 backdrop-blur-sm px-4 sm:px-5 py-3 sm:py-3.5 transition-all duration-300"
                data-testid={`supporting-client-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 group-hover:scale-125 transition-transform" />
                <span className="font-display text-base sm:text-lg lg:text-xl text-white font-medium tracking-wide whitespace-nowrap">
                  {c}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
